"use client";

import { AnimatePresence, m as motion, useInView, useReducedMotion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { Check, Plus } from "lucide-react";

// Scena in loop "dall'idea al prodotto" dentro una finestra di app:
// 0 idea (riquadri tratteggiati) → 1 prima versione (si riempiono, settimane 1-6)
// → 2 online (badge, utenti che salgono) → 3 cresce (nuove funzioni a ogni rilascio).
// È un esempio illustrativo: la didascalia lo dice.

const STEPS = ["Idea", "Prima versione", "Online", "Cresce"] as const;
const STEP_MS = [2600, 3600, 3200, 4200];
const FEATURES = ["v1.1 · Pagamenti", "v1.2 · Notifiche", "v1.3 · Area clienti"];
const BARS = [38, 62, 48, 80, 66, 92];

function useCountUp(target: number, run: boolean, ms = 1800) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!run) {
      setValue(0);
      return;
    }
    const start = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const p = Math.min(1, (now - start) / ms);
      setValue(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, ms]);
  return value;
}

export function ProductJourney() {
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-80px" });
  const reduce = useReducedMotion();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (reduce || !inView) return;
    const t = setTimeout(() => setStep((s) => (s + 1) % STEPS.length), STEP_MS[step]);
    return () => clearTimeout(t);
  }, [step, inView, reduce]);

  // Fuori schermo si riparte dall'inizio; con animazioni ridotte si mostra il prodotto finito.
  useEffect(() => {
    if (!inView) setStep(0);
  }, [inView]);
  const s = reduce ? 3 : step;

  const filled = s >= 1;
  const live = s >= 2;
  const week = useCountUp(6, s === 1 && !reduce, STEP_MS[1] - 600);
  const users = useCountUp(48, s === 2 && !reduce, STEP_MS[2] - 800);

  const dashed = "border border-dashed border-[hsl(var(--border))] bg-transparent";
  const solid = "border border-[hsl(var(--border))] bg-background/60";

  return (
    <div ref={ref} className="relative w-full max-w-[540px] select-none">
      <div
        className="pointer-events-none absolute -inset-8 rounded-full blur-3xl opacity-20"
        style={{ background: "hsl(var(--primary))" }}
      />

      <div
        role="img"
        aria-label="Esempio di percorso: dall'idea alla prima versione, poi online con i primi utenti e nuove funzioni a ogni rilascio."
        className="relative overflow-hidden rounded-2xl border border-[hsl(var(--border))] bg-surface shadow-[0_24px_80px_-24px_hsl(var(--primary)/0.4)]"
      >
        <div aria-hidden="true">
          {/* Barra della finestra */}
          <div className="flex items-center gap-2 border-b border-[hsl(var(--border))] px-4 py-3">
            <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]" />
            <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]" />
            <span className="ml-3 rounded-md bg-background/60 px-2.5 py-1 font-mono text-[11px] text-muted-foreground">
              il-tuo-prodotto.app
            </span>
            <span className="ml-auto font-mono text-[11px] text-muted-foreground">
              {s === 0 ? "Settimana 0" : s === 1 ? `Settimana ${Math.max(1, week)}` : "Settimana 6+"}
            </span>
          </div>

          {/* Fasi */}
          <div className="grid grid-cols-4 gap-1.5 px-4 pt-4">
            {STEPS.map((label, i) => (
              <div key={label} className="flex flex-col gap-1.5">
                <div className="h-1 overflow-hidden rounded-full bg-[hsl(var(--border))]">
                  <motion.div
                    className="h-full rounded-full"
                    style={{ background: "hsl(var(--primary))" }}
                    initial={false}
                    animate={{ width: i <= s ? "100%" : "0%" }}
                    transition={{ duration: i === s && !reduce ? STEP_MS[i] / 1000 : 0.3, ease: "linear" }}
                  />
                </div>
                <span
                  className={`font-mono text-[10px] uppercase tracking-[0.06em] transition-colors ${
                    i === s ? "text-primary" : "text-muted-foreground"
                  }`}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          {/* Area prodotto */}
          <div className="grid grid-cols-[88px_minmax(0,1fr)] gap-3 p-4 sm:grid-cols-[104px_minmax(0,1fr)]">
            {/* Menu laterale */}
            <div className={`flex flex-col gap-2 rounded-xl p-2.5 transition-all duration-500 ${filled ? solid : dashed}`}>
              {[0, 1, 2, 3].map((i) => (
                <motion.div
                  key={i}
                  className={`h-2.5 rounded-full transition-colors duration-500 ${
                    filled && i === 0 ? "bg-primary" : "bg-muted-foreground/35"
                  }`}
                  initial={false}
                  animate={{
                    opacity: filled ? 1 : 0.35,
                    width: filled ? ["70%", "55%", "62%", "48%"][i] : "60%",
                  }}
                  transition={{ duration: 0.5, delay: filled ? i * 0.08 : 0 }}
                />
              ))}
            </div>

            <div className="flex flex-col gap-3">
              {/* Stato e utenti */}
              <div className={`flex items-center gap-3 rounded-xl px-3 py-2.5 transition-all duration-500 ${filled ? solid : dashed}`}>
                <AnimatePresence mode="wait" initial={false}>
                  {live ? (
                    <motion.span
                      key="live"
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0 }}
                      className="inline-flex items-center gap-1.5 rounded-full bg-[hsl(var(--success)/0.12)] px-2.5 py-1 text-[11px] font-semibold text-[hsl(var(--success))]"
                    >
                      <span className="relative flex h-1.5 w-1.5">
                        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[hsl(var(--success))] opacity-70" />
                        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[hsl(var(--success))]" />
                      </span>
                      Online
                    </motion.span>
                  ) : (
                    <motion.span
                      key="draft"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="rounded-full bg-background/60 px-2.5 py-1 text-[11px] font-medium text-muted-foreground"
                    >
                      {filled ? "In costruzione" : "Da progettare"}
                    </motion.span>
                  )}
                </AnimatePresence>
                <span className="ml-auto text-right">
                  <span className="block text-[10px] uppercase tracking-[0.06em] text-muted-foreground">Utenti</span>
                  <span className="font-display text-lg font-semibold tabular-nums text-foreground">
                    {s === 3 ? 48 : live ? users : "–"}
                  </span>
                </span>
              </div>

              {/* Grafico */}
              <div className={`flex h-[104px] items-end gap-1.5 rounded-xl p-3 transition-all duration-500 sm:h-[120px] ${filled ? solid : dashed}`}>
                {BARS.map((h, i) => (
                  <motion.div
                    key={i}
                    className={`flex-1 rounded-t-md transition-colors duration-500 ${
                      filled ? "bg-primary/75" : "bg-muted-foreground/30"
                    }`}
                    initial={false}
                    animate={{
                      height: filled ? `${h}%` : "18%",
                      opacity: filled ? 1 : 0.3,
                    }}
                    transition={{ duration: 0.6, delay: filled ? 0.2 + i * 0.08 : 0 }}
                  />
                ))}
              </div>

              {/* Rilasci */}
              <div className="flex min-h-[64px] flex-col gap-1.5">
                <AnimatePresence initial={false}>
                  {s === 3 &&
                    FEATURES.map((f, i) => (
                      <motion.div
                        key={f}
                        initial={{ opacity: 0, x: 16 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, delay: reduce ? 0 : 0.3 + i * 0.7 }}
                        className="flex items-center gap-2 rounded-lg bg-primary/10 px-2.5 py-1.5 text-[12px] font-medium text-foreground"
                      >
                        <Plus className="h-3.5 w-3.5 text-primary" />
                        {f}
                      </motion.div>
                    ))}
                  {s === 2 && (
                    <motion.div
                      key="deploy"
                      initial={{ opacity: 0, y: 6 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className="flex items-center gap-2 rounded-lg bg-[hsl(var(--success)/0.1)] px-2.5 py-1.5 font-mono text-[11.5px] text-foreground"
                    >
                      <Check className="h-3.5 w-3.5 text-[hsl(var(--success))]" />
                      Pubblicato in produzione
                    </motion.div>
                  )}
                  {s < 2 && (
                    <motion.p
                      key="hint"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      className="px-1 text-[12px] text-muted-foreground"
                    >
                      {s === 0 ? "Partiamo da una call di 30 minuti." : "Solo le funzioni che servono per partire."}
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p className="mt-4 text-center font-mono text-[11px] uppercase tracking-[0.08em] text-muted-foreground">
        Esempio di percorso
      </p>
    </div>
  );
}
